import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import { Tooltip, Button, Label, TextInput, Textarea, Spinner } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import Select from "react-select";
import { Plus } from "@phosphor-icons/react";

import { Table } from "../../../components/Table";
import { useSupportTickets } from "../../../hooks/get/useSupportTickets";
import { ISupportTicketDetail } from "../../../hooks/get/useSupportTickets";
import { DEFAULT_ITEMS_PER_PAGE, customSelectStyles } from "../../../shared/constants";
import AnimatedModal from "../../../components/AnimatedModal";
import { useAddSupportTicket, useAuth, useIntegrations } from "../../../hooks";
import { ISupportTicket, TSupportCategory } from "../../../interfaces";
import DynamicFilter, { DynamicFilterField, DynamicFilterValues } from "../../../components/DynamicFilter";

const schema = yup.object({
    subject: yup.string().required("Subject is required"),
    category: yup.string().oneOf(["general", "technical", "billing", "feature_request", "bug_report", "account", "other"]).required("Category is required"),
    message: yup.string().required("Message is required"),
    pair_name: yup.string().optional(),
    tracking_id: yup.string().optional(),
}).required();

const categoryOptions: { value: TSupportCategory; label: string }[] = [
    { value: "general", label: "General" },
    { value: "technical", label: "Technical" },
    { value: "feature_request", label: "Feature Request" },
    { value: "bug_report", label: "Bug Report" },
    { value: "account", label: "Account" },
    { value: "other", label: "Other" },
];

const statusOptions = [
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
];

const SupportTicketsList: React.FC = () => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { user } = useAuth();
    const { data: integrations } = useIntegrations();
    const { mutate: addTicket, isPending } = useAddSupportTicket();

    const [filters, setFilters] = useState<DynamicFilterValues>({
        status: null,
        category: null,
    });

    const {
        register,
        handleSubmit,
        reset,
        control,
        setValue,
        formState: { errors },
    } = useForm<ISupportTicket>({
        resolver: yupResolver(schema) as any,
        defaultValues: {
            category: "general" as TSupportCategory,
            subject: "",
            message: "",
            pair_name: "",
            tracking_id: "",
        }
    });

    const integrationOptions = useMemo(() => {
        if (!integrations) return [];
        return Object.entries(integrations).map(([trackingId, set]) => {
            const label = set.name
            return {
                value: trackingId,
                label: label,
                pair_name: label,
            };
        });
    }, [integrations]);

    const filterFields = useMemo<DynamicFilterField[]>(() => [
        {
            id: "status",
            label: "Status",
            type: "select",
            options: statusOptions,
            value: statusOptions.find(opt => opt.value === filters.status) || null,
        },
        {
            id: "category",
            label: "Category",
            type: "select",
            options: categoryOptions,
            value: categoryOptions.find(opt => opt.value === filters.category) || null,
        },
    ], [filters]);

    const handleFilterApply = (values: DynamicFilterValues) => {
        setFilters({
            status: (values.status as any)?.value || null,
            category: (values.category as any)?.value || null,
        });
    };

    const handleResetFilters = () => {
        setFilters({
            status: null,
            category: null,
        });
    };

    const hasActiveFilters = useMemo(() => {
        return Object.values(filters).some(value => value !== null);
    }, [filters]);

    const onSubmit = (data: ISupportTicket) => {
        addTicket(data, {
            onSuccess: () => {
                toast.success(t("common.supportTicketCreated", "Support ticket created successfully!"));
                reset({
                    subject: "",
                    category: "general",
                    message: "",
                    pair_name: "",
                    tracking_id: "",
                });
                setIsModalOpen(false);
                queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
            },
            onError: (error: any) => {
                toast.error(error.message || "Failed to create support ticket");
            }
        });
    };

    const ticketParams = useMemo(() => {
        const params: any = { per_page: 999999 };
        if (filters.status) params.status = filters.status;
        if (filters.category) params.category = filters.category;
        return params;
    }, [filters]);

    const { data, isLoading } = useSupportTickets(ticketParams);

    const renderTruncatedCell = (text: string | null) => {
        if (!text) return "-";
        return (
            <Tooltip content={<div className="max-w-xs break-words">{text}</div>} placement="top">
                <span className="block truncate cursor-help" style={{ maxWidth: "200px" }}>
                    {text}
                </span>
            </Tooltip>
        );
    };

    const columns = React.useMemo<ColumnDef<ISupportTicketDetail>[]>(
        () => [
            {
                accessorKey: "subject",
                header: t("support.table.subject"),
                size: 200,
                cell: ({ row }) => renderTruncatedCell(row.original.subject),
            },
            {
                accessorKey: "message",
                header: t("support.table.message"),
                size: 250,
                cell: ({ row }) => renderTruncatedCell(row.original.message),
            },
            {
                accessorKey: "status",
                header: t("support.table.status"),
                size: 130,
                cell: ({ row }) => {
                    const status = row.original.status;
                    const statusDisplay = row.original.status_display;

                    const getStatusColor = (status: string) => {
                        switch (status) {
                            case "resolved":
                            case "closed":
                                return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
                            case "in_progress":
                                return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
                            case "open":
                                return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
                            default:
                                return "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300";
                        }
                    };

                    return (
                        <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                                status
                            )}`}
                        >
                            {statusDisplay}
                        </span>
                    );
                },
            },
            {
                accessorKey: "pair_name",
                header: t("support.table.pair_name"),
                size: 180,
            },
            {
                accessorKey: "category",
                header: t("support.table.category"),
                size: 130,
                cell: ({ row }) => {
                    return <span className="capitalize">{row.original.category}</span>;
                },
            },
            {
                accessorKey: "admin_remarks",
                header: t("support.table.admin_remarks"),
                size: 250,
                cell: ({ row }) => renderTruncatedCell(row.original.admin_remarks),
            },
            {
                accessorKey: "created_at",
                header: t("support.table.created_at"),
                size: 130,
                cell: ({ row }) => {
                    return row.original.created_at
                        ? new Date(row.original.created_at).toLocaleDateString()
                        : "-";
                },
            },
            {
                accessorKey: "resolved_at",
                header: t("support.table.resolved_at"),
                size: 130,
                cell: ({ row }) => {
                    return row.original.resolved_at
                        ? new Date(row.original.resolved_at).toLocaleDateString()
                        : "-";
                },
            },
        ],
        [t]
    );

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <Table
                data={data?.results || []}
                columns={columns}
                isLoading={isLoading}
                showPagination={true}
                showSearch={true}
                prependWithSearch={
                    <div className="flex items-center gap-2">
                        <DynamicFilter
                            title="Filters"
                            fields={filterFields}
                            onApply={handleFilterApply}
                            t={t}
                        />
                        {hasActiveFilters && (
                            <Button
                                color="light"
                                onClick={handleResetFilters}
                                className="h-[42px] !flex items-center justify-center"
                            >
                                <span className="text-red-600">Reset</span>
                            </Button>
                        )}
                    </div>
                }
                appendWithSearch={
                    <Button
                        color="indigo"
                        onClick={() => setIsModalOpen(true)}
                        className="h-[42px] !flex items-center justify-center"
                    >
                        <div className="flex items-center">
                            <Plus className="mr-2" size={18} weight="bold" />
                            <span>{t("support.addTicket", "Add Ticket")}</span>
                        </div>
                    </Button>
                }
                pageSize={DEFAULT_ITEMS_PER_PAGE}
                maxHeight="calc(100vh - 300px)"
            />

            <AnimatedModal
                show={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title="Add Support Ticket"
                size="lg"
            >
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="space-y-3"
                >
                    <div className="grid grid-cols-1 gap-1">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                            <div>
                                <Label htmlFor="hc_name" value="Name" className="mb-1 block" />
                                <TextInput
                                    id="hc_name"
                                    value={user?.name || ""}
                                    readOnly
                                    disabled
                                    className="bg-gray-50"
                                />
                            </div>
                            <div>
                                <Label htmlFor="hc_email" value="Email" className="mb-1 block" />
                                <TextInput
                                    id="hc_email"
                                    value={user?.email || ""}
                                    readOnly
                                    disabled
                                    className="bg-gray-50"
                                />
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="hc_subject" value="Subject" className="mb-1 block" />
                            <TextInput
                                id="hc_subject"
                                placeholder="Brief summary of your issue"
                                {...register("subject")}
                                color={errors.subject ? "failure" : "gray"}
                            />
                            <div className="min-h-[20px] mt-1">
                                {errors.subject && (
                                    <p className="text-xs text-red-600">{errors.subject.message}</p>
                                )}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="hc_category" value="Category" className="mb-1 block" />
                                <Controller
                                    name="category"
                                    control={control}
                                    render={({ field }) => (
                                        <Select<{ value: TSupportCategory; label: string }>
                                            id="hc_category"
                                            options={categoryOptions}
                                            styles={customSelectStyles()}
                                            value={categoryOptions.find((opt) => opt.value === field.value) || null}
                                            onChange={(val) => field.onChange(val?.value)}
                                            classNamePrefix="custom-select"
                                            isSearchable={false}
                                        />
                                    )}
                                />
                                <div className="min-h-[20px] mt-1">
                                    {errors.category && (
                                        <p className="text-xs text-red-600">{errors.category.message}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="hc_integration" value="Integration (Optional)" className="mb-1 block" />
                                <Controller
                                    name="tracking_id"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            id="hc_integration"
                                            options={integrationOptions}
                                            styles={customSelectStyles()}
                                            value={integrationOptions.find((opt) => opt.value === field.value) || null}
                                            onChange={(val: any) => {
                                                field.onChange(val?.value || "");
                                                setValue("pair_name", val?.pair_name || "");
                                            }}
                                            classNamePrefix="custom-select"
                                            isSearchable={true}
                                            placeholder="Select Integration"
                                            isClearable={true}
                                        />
                                    )}
                                />
                                <div className="min-h-[20px] mt-1">
                                    {errors.tracking_id && (
                                        <p className="text-xs text-red-600">{errors.tracking_id.message}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div>
                            <Label htmlFor="hc_message" value="Message" className="mb-1 block" />
                            <Textarea
                                id="hc_message"
                                rows={6}
                                placeholder="Describe the issue you’re facing, steps to reproduce, and any screenshots/links."
                                {...register("message")}
                                color={errors.message ? "failure" : "gray"}
                            />
                            <div className="min-h-[20px] mt-1">
                                {errors.message && (
                                    <p className="text-xs text-red-600">{errors.message.message}</p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-end gap-2 mt-4">
                        <Button color="light" type="button" onClick={() => {
                            setIsModalOpen(false);
                            reset();
                        }}>Cancel</Button>
                        <Button
                            color="indigo"
                            type="submit"
                            disabled={isPending}
                            isProcessing={isPending}
                            processingSpinner={<Spinner size="sm" />}
                        >
                            Submit Ticket
                        </Button>
                    </div>
                </form>
            </AnimatedModal>
        </div>
    );
};

export default SupportTicketsList;

